import { BookOpen, FilePen } from "lucide-react";
import { useTranslation } from "react-i18next";

/**
 * What an authorization lets a client do (mcp-write-tools D2, mcp-foundations-files D11), on the
 * consent screen and the device page: reading, always; creating and editing as the user, never
 * deleting, sharing, publishing or changing members, only when the client asked for the MCP
 * write scope. `/api` is everything the user can do in ESI (#174), said so.
 * Plain icon-and-text lines, never a badge: the consequences of writing are stated where the
 * user decides.
 */
export function PermissionList({
  scopes,
  resource,
}: {
  scopes: readonly string[] | undefined;
  resource: "api" | "mcp";
}) {
  const { t } = useTranslation();
  const write = scopes?.includes("write") ?? false;
  return (
    <ul className="flex flex-col gap-2">
      <li className="flex gap-2">
        <BookOpen
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-fg-muted"
          strokeWidth={1.75}
        />
        <span className="text-fg">
          {t(resource === "api" ? "connect.permissions.everything" : "connect.permissions.read")}
        </span>
      </li>
      {write ? (
        <li className="flex gap-2">
          <FilePen
            aria-hidden="true"
            className="mt-0.5 size-4 shrink-0 text-fg-muted"
            strokeWidth={1.75}
          />
          <span className="flex min-w-0 flex-col gap-0.5">
            <span className="font-medium text-fg">{t("connect.permissions.write")}</span>
            <span className="text-xs leading-relaxed text-fg-muted">
              {t("connect.permissions.writeHint")}
            </span>
          </span>
        </li>
      ) : null}
    </ul>
  );
}

/** The confirmation's `write`: true only when the screen named the write permission. */
export const writeConfirmed = (scopes: readonly string[] | undefined) =>
  scopes?.includes("write") ?? false;
